/*
 * File: c:\Users\j.mazat\Desktop\Moby\MobyFleet\mobyfleet_backoffice\src\Content\Offers\OfferFiles\OfferFiles.tsx
 * Project: c:\Users\j.mazat\Desktop\Moby\MobyFleet\mobyfleet_backoffice
 * Created Date: Thursday October 10th 2024
 * Author: Jonathan Mazat
 * -----
 * Last Modified: Thursday October 10th 2024 10:26:38 am
 * Modified By: the developer formerly known as Jonathan Mazat at <you@you.you>
 * -----
 * Copyright (c) 2024 Autobuy SAS
 * -----
 */

import { useEffect, useMemo, useState } from "react";
import { Button, Input, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import LoadingScreen from "Content/LoadingScreen/LoadingScreen";
import ModalConfirmation from "Content/Modals/ModalConfirmation/ModalConfirmation";
import translator from "MultiLanguage/TranslationWrapper";
import { OfferFileType } from "Type/offer.type";
import OfferFilesRow from "./OfferFilesRow";

type OfferFilesProps = {
    offerID: string,
    changeFileCount: Function,
    getter: Function,
    saving: Function
}

export default function OfferFiles(props: OfferFilesProps) {
    const [files, setFiles] = useState<OfferFileType[]>([]);
    const [isSendable, setIsSendable] = useState(false);
    const [upload, setUpload] = useState<FileList>();
    const [isOkay, setIsOkay] = useState<boolean>(false);
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [additionnalText, setAdditionnalText] = useState<string | string[] | undefined>();
    const [spinner, setSpinner] = useState(false);

    const headers = ["Name", "Shared", "Action"];

    const handleResponse = (text: string | string[], isOkay: boolean = false) => {
        setIsOkay(isOkay);
        setOpenModal(true);
        setSpinner(false);
        setAdditionnalText(text);
    };

    const handleDeleteRow = (id: string) => {
        const tmp = files.filter(x => id !== x.id);
        setFiles([...tmp]);
    };

    const buildRows = (files: OfferFileType[]) => {
        const offerFileToDisplay: JSX.Element[] = [];
        for (let i = 0; i < files.length; ++i)
            offerFileToDisplay.push(
                <OfferFilesRow key={`${files[i].id}_${Math.random()}`} file={files[i]} changeFileCount={props.changeFileCount} removeFileRow={handleDeleteRow} />
            );
        return offerFileToDisplay;
    };

    const offerValues = useMemo(() => {
        return buildRows(files);
    }, [files]);

    useEffect(() => {
        props.getter().then((result: {data: OfferFileType[]}) => {
            setFiles(result.data);
        }).catch((error: any) => {
            console.error(error);
        });
    }, []);

    function setFilePicking(e: any) {
        if (e.target.files.length > 0) {
            setUpload(e.target.files);
            setIsSendable(true);
        } else {
            setUpload(undefined);
            setIsSendable(false);
        }
    };

    const HandleCloseModal = () => {
        setOpenModal(false);
    };

    const HandleSubmit = (e: any) => {
        e.preventDefault();
        setSpinner(true);
        if (upload) {
            props.saving(upload).then((result: { data: { fileAdded: {value: OfferFileType}[]; failedFile: string[]; }; }) => {
                props.changeFileCount(result.data.fileAdded.length);
                setFiles([...files, ...result.data.fileAdded.map(x => x.value)]);
                const textArray = [`${result.data.fileAdded.length} ${translator.decode("response.fileAdded")}`];
                if (result.data.failedFile.length !== 0)
                    textArray.push(translator.decode("response.error"), ...result.data.failedFile);
                handleResponse(textArray, true);
            }).catch((error: { message: string; }) => {
                console.error(error);
                handleResponse([translator.decode("response.error"), error.message]);
            });
        }
    };

    return (
        <>
            {files.length !== 0 ?
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {headers.map(x => <TableCell key={`${x}_${Math.random()}`} align="center"><strong>{translator.decode(`table.headers.${x}`)}</strong></TableCell>)}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {offerValues}
                    </TableBody>
                </Table>
                :null
            }
            <div className="pt-3 pb-3">
                <Input type="file" inputProps={{ multiple: true }} onChange={setFilePicking} />
                {
                    spinner ?
                        <LoadingScreen width="2rem" height="2rem" borderWidth="5px" />
                    :null
                }
                <Button disabled={!isSendable} onClick={(e) => {HandleSubmit(e)}}>{translator.decode("dynamic.send")}</Button>
            </div>
            <ModalConfirmation show={openModal} onClose={HandleCloseModal} closeModal={HandleCloseModal} isOkay={isOkay} additionnalMessage={additionnalText}/>
        </>
    );
};
